// App
import type React from 'react';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { createRef, useEffect, useRef, useState } from 'react';

// Actions
import { setActiveId } from '../flux/actions/UIActions';
import { useAppDispatch, useAppSelector } from '../flux/configureStore';

// Types
import type ComparablesModelType from '../models/Comparables';

type RefsObjectType = {
  [key: string]: React.RefObject<HTMLDivElement>;
};

type HandleActiveItem = (activeRef: RefObject<HTMLDivElement>, snapToItem?: boolean) => void;

const CARD_WIDTH = 160; //px
export const useItemRefs = (comps: Array<ComparablesModelType>) => {
  const _getRefs = () => {
    return comps.reduce((accu: RefsObjectType, value: ComparablesModelType) => {
      const id = value.listingId;

      if (id) {
        accu[id] = createRef();
      }

      return accu;
    }, {});
  };
  const [itemRefs, setItemRefs] = useState(_getRefs());

  useEffect(() => {
    const newRefs = _getRefs();
    setItemRefs(newRefs);
  }, [comps]);

  return [itemRefs];
};

export const useCarousel = (parentContainerRef: React.RefObject<HTMLDivElement>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [homePosition, updateHomePosition] = useState(-1);

  const setHomePosition = (homeRef: React.RefObject<HTMLDivElement>): void => {
    // scroll to home card
    handleActiveItem(homeRef, true);

    // update new home position
    containerRef.current && updateHomePosition(containerRef.current.scrollLeft);
  };

  const handleActiveItem = (activeRef: React.RefObject<HTMLDivElement>, snapToItem?: boolean): void => {
    if (activeRef && activeRef.current) {
      const LISTING_CARD_HALF_WIDTH = activeRef.current.offsetWidth / 2;
      const PARENT_HALF_WIDTH = (parentContainerRef.current && parentContainerRef.current.offsetWidth / 2) || 0;
      containerRef.current &&
        containerRef.current.scrollTo({
          top: 0,
          left: activeRef.current.offsetLeft - PARENT_HALF_WIDTH + LISTING_CARD_HALF_WIDTH,
          behavior: snapToItem ? 'auto' : 'smooth',
        });
    }
  };

  return {
    containerRef,
    homePosition,
    setHomePosition,
    handleActiveItem,
  };
};

export const useButtons = (containerRef: React.RefObject<HTMLDivElement>, homePosition: number) => {
  // Redux
  const dispatch = useAppDispatch();
  const homeId = useAppSelector((state) => state.address.listingId);

  // State
  const [hasPrev, updateHasPrev] = useState(false);
  const [hasNext, updateHasNext] = useState(false);
  const [showHomeLeft, setShowHomeLeft] = useState(false);
  const [showHomeRight, setShowHomeRight] = useState(false);
  const containerWidth = (containerRef.current && containerRef.current.clientWidth + 4) || 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const checkScrollPosition = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
    }

    timer = setTimeout(() => {
      const currentRef = containerRef.current;
      const isCarouselStart = currentRef && currentRef.scrollLeft === 0;
      const isCarouselEnd = currentRef && currentRef.scrollWidth - currentRef.scrollLeft - currentRef.offsetWidth === 0;

      isCarouselEnd ? updateHasNext(false) : updateHasNext(true);
      isCarouselStart ? updateHasPrev(false) : updateHasPrev(true);

      currentRef && checkHomePosition(currentRef.scrollLeft);
    }, 50);
  };

  const checkHomePosition = (scrollPosition: number): void => {
    const carouselWidth = containerRef.current && containerRef.current.offsetWidth;
    const halfWidth = (carouselWidth && carouselWidth / 2 + CARD_WIDTH / 2) || 10;

    if (scrollPosition < homePosition - halfWidth) {
      setShowHomeLeft(false);
      setShowHomeRight(true);
    } else if (scrollPosition > homePosition + halfWidth) {
      setShowHomeLeft(true);
      setShowHomeRight(false);
    } else {
      setShowHomeLeft(false);
      setShowHomeRight(false);
    }
  };

  const handlePrev = (): void => {
    containerRef.current &&
      containerRef.current.scrollTo({
        left: containerRef.current.scrollLeft - containerWidth,
        behavior: 'smooth',
      });
  };

  const handleNext = (): void => {
    containerRef.current &&
      containerRef.current.scrollTo({
        left: containerRef.current.scrollLeft + containerWidth,
        behavior: 'smooth',
      });
  };

  const handleHome = (): void => {
    dispatch(setActiveId(homeId));
    containerRef.current &&
      containerRef.current.scrollTo({
        top: 0,
        left: homePosition,
        behavior: 'smooth',
      });
  };

  return {
    checkScrollPosition,
    handlePrev,
    handleNext,
    handleHome,
    hasPrev,
    hasNext,
    showHomeLeft,
    showHomeRight,
  };
};

export const useScrollAlert = () => {
  const [showAlert, setShowAlert] = useState(false);

  const handleScrollAlert = (bool: boolean) => {
    setShowAlert(bool);
  };

  return { showAlert, handleScrollAlert };
};

export const useScrollRefresh = (
  isShowing: boolean,
  ref: RefObject<HTMLDivElement>,
  handleActiveItem: HandleActiveItem,
  checkScrollPosition: () => void,
  setMachineScroll: Dispatch<SetStateAction<boolean>>,
) => {
  useEffect(() => {
    if (isShowing) {
      // refresh in case user selects property while carousel is hidden
      handleActiveItem(ref, true);
      checkScrollPosition();
      setMachineScroll(true);
    }
  }, [isShowing]);
};
